import { z } from 'zod'

/**
 * JSONB type for the `labels` column on legacy_site_logger_history_ and
 * legacy_site_cable_history_. Each label describes a sensor position on a
 * cable/logger installation at a site.
 */
export type LegacySiteHistoryLabel = {
  note?: string
  number?: number
  visible?: boolean
  [key: string]: unknown // MongoDB documents may have extra fields
}

export type LegacySiteHistoryLabels = LegacySiteHistoryLabel[]

/**
 * Backward-compatible aliases -- the table was renamed from site_ to legacy_site_
 * but the branded ID type is a semantic concept used across the entire codebase.
 * Per spec Section 4.9: "Keep SiteId as the branded type."
 */
export type { LegacySiteId as SiteId } from '../schemas/public/LegacySite.js'
export { legacySiteId as siteId } from '../schemas/public/LegacySite.js'

// ---------------------------------------------------------------------------
// Write-path validation for site history entries
//
// The Joi schema that guarded these writes was lost when @beaded/js-validate
// was replaced by zod (#2950) and sites moved to Postgres (#3009). The
// generated history schemas type `labels` as z.unknown() and coerce dates with
// z.coerce.date(), so bad labels, epoch numbers and '10/01/2026' got through.
// The schemas below are applied in hybrids/ExtendedSite.ts to every schema the
// site write endpoints parse with (generated files under src/schemas must not
// be hand-edited). They follow the old Joi rules except, deliberately:
//   - a whitespace-only note is dropped (Joi dropped only the empty string);
//   - unknown label keys are kept, not rejected (one prod label has extras);
//   - lineGapSize '' is rejected (Joi defaulted it to 25); null clears it, and
//     the charts then use their 25 h default;
//   - history dates must be ISO 8601 (a zone-less date-time is read as UTC);
//   - a null note or null visible is treated as absent, so stored entries
//     echoed back by the edit forms still save.
// ---------------------------------------------------------------------------

type IssueCtx = z.core.$RefinementCtx<unknown>

/** Limits on one history entry's labels (a cable has a few dozen sensors). */
export const MAX_LABELS_PER_ENTRY = 1000
export const MAX_LABEL_NOTE_LENGTH = 500

const BLANK = /^\s*$/
// A plain decimal number as a form or CSV would write it: no hex, no
// thousands separators, no stray text. Exponents are allowed ('1e3').
const NUMERIC_STRING = /^[+-]?(?:\d+\.?\d*|\.\d+)(?:e[+-]?\d+)?$/i

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v)

/**
 * Normalises one label. Returns undefined (after adding an issue) when invalid.
 * Rules:
 *  - note: string; blank / whitespace-only / null -> key omitted; else 400.
 *  - number: finite number or numeric string -> number; '' / null / undefined
 *    -> key omitted; anything else (e.g. 'abc', NaN) -> 400.
 *  - visible: boolean or the strings 'true' / 'false' (any case) -> boolean;
 *    missing or
 *    null -> true; anything else -> 400.
 *  - unknown keys pass through unchanged.
 */
const normalizeLabel = (
  raw: unknown,
  index: number,
  ctx: IssueCtx,
): LegacySiteHistoryLabel | undefined => {
  const fail = (key: string | null, message: string) => {
    ctx.addIssue({
      code: 'custom',
      message,
      path: key === null ? [index] : [index, key],
    })
    return undefined
  }

  if (!isPlainObject(raw)) return fail(null, 'label must be an object')

  const { note, number, visible, ...rest } = raw
  const out: LegacySiteHistoryLabel = { ...rest }

  if (note !== undefined && note !== null) {
    if (typeof note !== 'string') return fail('note', 'note must be a string')
    if (note.length > MAX_LABEL_NOTE_LENGTH)
      return fail(
        'note',
        `note must be at most ${MAX_LABEL_NOTE_LENGTH} characters`,
      )
    if (!BLANK.test(note)) out.note = note
  }

  if (number !== undefined && number !== null) {
    if (typeof number === 'number') {
      if (!Number.isFinite(number))
        return fail('number', 'number must be a finite number')
      out.number = number
    } else if (typeof number === 'string') {
      const trimmed = number.trim()
      if (trimmed !== '') {
        if (!NUMERIC_STRING.test(trimmed))
          return fail('number', 'number must be numeric')
        const n = Number(trimmed)
        if (!Number.isFinite(n))
          return fail('number', 'number must be a finite number')
        out.number = n
      }
    } else {
      return fail('number', 'number must be a number')
    }
  }

  if (visible === undefined || visible === null) out.visible = true
  else if (typeof visible === 'boolean') out.visible = visible
  else if (typeof visible === 'string' && visible.toLowerCase() === 'true')
    out.visible = true
  else if (typeof visible === 'string' && visible.toLowerCase() === 'false')
    out.visible = false
  else return fail('visible', "visible must be a boolean or 'true'/'false'")

  return out
}

/**
 * `labels` on legacy_site_cable_history_ / legacy_site_logger_history_ writes.
 * null and undefined pass through (undefined = leave unchanged on update,
 * null = clear). Anything but an array of objects is rejected.
 */
export const legacySiteHistoryLabelsSchema = z
  .unknown()
  .transform((raw, ctx): LegacySiteHistoryLabels | null | undefined => {
    if (raw === undefined) return undefined
    if (raw === null) return null
    if (!Array.isArray(raw)) {
      ctx.addIssue({ code: 'custom', message: 'labels must be an array' })
      return z.NEVER
    }
    if (raw.length > MAX_LABELS_PER_ENTRY) {
      ctx.addIssue({
        code: 'custom',
        message: `labels must have at most ${MAX_LABELS_PER_ENTRY} entries`,
      })
      return z.NEVER
    }
    const before = ctx.issues.length
    const labels: LegacySiteHistoryLabels = []
    raw.forEach((item, i) => {
      const label = normalizeLabel(item, i, ctx)
      if (label) labels.push(label)
    })
    return ctx.issues.length > before ? z.NEVER : labels
  })

/**
 * History dates must be after this instant (Joi's `min` on the old schema).
 * Exported as a string so no importer can mutate a shared Date.
 */
export const LEGACY_SITE_HISTORY_MIN_DATE_ISO = '2000-01-01T00:00:00Z'
const MIN_DATE_MS = Date.parse(LEGACY_SITE_HISTORY_MIN_DATE_ISO)

// ISO 8601 calendar date, optionally with a time and a zone offset.
const ISO_8601 =
  /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d{1,9})?)?(Z|[+-]\d{2}:\d{2})?)?$/

const parseIsoDate = (value: string): Date | null => {
  const m = ISO_8601.exec(value)
  if (!m) return null
  const [, y, mo, d, h = '0', mi = '0', s = '0', zone] = m
  const year = Number(y)
  const month = Number(mo)
  const day = Number(d)
  // Date.UTC rolls 2026-02-30 over to March; round-trip to catch it.
  const check = new Date(Date.UTC(year, month - 1, day))
  if (
    check.getUTCFullYear() !== year ||
    check.getUTCMonth() !== month - 1 ||
    check.getUTCDate() !== day
  )
    return null
  if (Number(h) > 23 || Number(mi) > 59 || Number(s) > 59) return null
  // A zone-less date-time is UTC, never the server's local time.
  const iso = m[4] !== undefined && zone === undefined ? `${value}Z` : value
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? null : date
}

/**
 * startedAt / endedAt on a site history entry. Accepts a Date or an ISO 8601
 * string (date or date-time); rejects epoch numbers, non-ISO strings
 * ('10/01/2026') and anything not after LEGACY_SITE_HISTORY_MIN_DATE_ISO.
 * Always yields a Date. null and undefined are handled by the callers'
 * .nullable() / .optional().
 */
export const legacySiteHistoryDateSchema = z
  .unknown()
  .transform((raw, ctx): Date => {
    let date: Date | null = null
    if (raw instanceof Date) {
      date = Number.isNaN(raw.getTime()) ? null : raw
    } else if (typeof raw === 'string') {
      date = parseIsoDate(raw)
    } else if (raw === undefined) {
      ctx.addIssue({ code: 'custom', message: 'Required' })
      return z.NEVER
    }
    if (!date) {
      ctx.addIssue({
        code: 'custom',
        message: 'must be an ISO 8601 date or date-time string',
      })
      return z.NEVER
    }
    if (date.getTime() <= MIN_DATE_MS) {
      ctx.addIssue({ code: 'custom', message: 'must be after 2000-01-01' })
      return z.NEVER
    }
    return date
  })

// Whitespace plus the zero-width characters String.trim() leaves in place
// (U+200B, U+200C, U+200D, U+FEFF), written as escapes to keep the source ASCII.
const BLANK_SITE_NAME = /^(?:\s|\u200b|\u200c|\u200d|\ufeff)*$/

/** Non-blank after trimming; the stored value is left as the client sent it. */
export const legacySiteNameSchema = z
  .string()
  .refine((s) => !BLANK_SITE_NAME.test(s), 'siteName must not be blank')

/** line_gap_size is an INTEGER column. */
export const legacySiteLineGapSizeSchema = z.int32().nullable().optional()
